import {
  CardTypeCallToActions,
  Learning_Type,
  Product,
  RouteTypes,
  SessionStorageKeys,
  SubscriptionType,
} from '@app/enums/_index';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { Course, SearchObject, User } from '@app/models/_index';
import { CourseAdapter, SearchObjectAdapter } from '@app/adapters/_index';
import { CourseInterface, HasAnalytics, SearchObjectInterface } from '@app/interfaces/_index';
import {
  CourseService,
  EnrollmentService,
  GoogleAnalyticsService,
  UserService,
} from '@app/services/_index';
import { compareSwap, logGoogleAnalytics } from '@app/utilities/_index';

import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { environment } from '@environments/environment';

@Component({
  selector: 'app-recommended-foundational-courses',
  templateUrl: './recommended-foundational-courses.component.html',
  styleUrls: ['./recommended-foundational-courses.component.scss'],
  standalone: false,
})
export class RecommendedFoundationalCoursesComponent implements OnInit, OnDestroy, HasAnalytics {
  // Current user
  currentUser: User;
  @Input() title: string;
  @Input() description: string;
  @Input() selectedSubscription: string; // Selected subscription product needed to display in this component
  @Input() selectedSubscriptionSlug: string[];
  @Input() queryParams: string;
  @Input() showViewAllButton = true;
  @Input() showButton = true;
  @Input() courseIds: number[] = [];

  @Output() sendClick = new EventEmitter<boolean>();

  subs: Subscription[] = [];

  parentSubscriptionId: number;
  parentSubscriptionSlugName: string;

  relatedCourseIds: number[] = [];
  courses: Course[] = [];
  showViewAllCourses = true;

  // Based on the selected subscription load the associated first 3 foundational courses and 1st certified exam
  // NOTE: The first engage foundation courses and last one is exam: [184, 187, 190, 11647]
  // NOTE: The first pureconnect foundation courses and last one is exam: [344, 345, 346, 11641]
  // NOTE: The first genesys_cloud foundation courses and last one is exam: [135, 140, 143, 11501]

  constructor(
    private courseService: CourseService,
    private userService: UserService,
    private enrollmentService: EnrollmentService,
    private googleAnalyticsService: GoogleAnalyticsService,
    private courseAdapter: CourseAdapter,
    private searchObjectAdapter: SearchObjectAdapter,
    public router: Router
  ) {}

  /**
   * OnInit
   * Load recommended courses and exam per selected subscription
   */
  async ngOnInit(): Promise<void> {
    // NOTE: User service handles and knows the authentication service, subscription service,
    // course service and enrollment service. This keeps the session docebo token in sync
    // on refresh or reload.
    // NOTE: Do not use firstValueFrom if user is not signed in, it will silently fail.
    // Using the pipe, catchError and return of(null) with firstValueFrom will not work either.
    // This will handle both signed in and un-assigned users.
    this.userService.doceboUser$.subscribe({
      next: (results) => {
        this.currentUser = results;
      },
      error: (err) => {
        console.log('undefined user', err);
      },
      complete: () => {},
    });

    // Load foundation course ids by selected subscription
    this.loadCourseIdsBySubscription();
    // Load courses by course ids
    this.loadCourses();
  }

  /**
   * Load Recommended Course by course ids from environment configurations
   */
  loadCourses(): void {
    if (this.courseIds && this.courseIds.length > 0) {
      // Gather and push the related course ids to the same course ids array to
      // send to the course service to get a collection of related courses
      const allCourseIds = this.courseIds.slice();
      this.subs.push(
        this.courseService.getCourses(allCourseIds).subscribe({
          next: (courses: CourseInterface[]) => {
            // Related Courses are already in order by Course Ids. See environment.ts
            const searchObjectsMap: Map<string, SearchObject> = new Map<string, SearchObject>();
            for (const courseItem of courses) {
              let response: CourseInterface = courseItem;
              const course: Course = this.courseAdapter.adapt(response);
              // NOTE: make sure parent subscription id and slug name are set. See loadCourseIdsBySubscription
              course.subscriptionId = this.parentSubscriptionId;
              course.subscriptionSlugName = this.parentSubscriptionSlugName;

              let searchbjectResponse: SearchObjectInterface = courseItem;
              const item: SearchObject = this.searchObjectAdapter.adapt(searchbjectResponse);
              // Re-attach and update the search object to have reference to the subscription
              // id and slug
              // TODO: Fix this in the search object adapter to set this
              item.parentId = this.parentSubscriptionId;
              item.parentSlug = this.parentSubscriptionSlugName;

              // Set slug name and course id for course detail
              // Don't pass course id in route query params
              // TODO: Need to clear this out and amend this as Search Object Adapter
              sessionStorage.setItem(course.slugName, String(course.id));

              if (!searchObjectsMap.has(item.code)) {
                searchObjectsMap.set(item.code, item);
                this.courses.push(course);
              } else {
                // If the key exists, get the last course object and update related course id
                // NOTE: Handle Self-study and ILT courses. They keys or the course names
                // are not necessarily in alphabetical order. If the previous code is self study
                // then you need to swap that map key with the one that is alpabetically in the correct order
                // and swap the keep and default object
                // Compare previous and incoming items by course name, specifically for Self-Study and Instructor Led (ILT)
                // Data can be pre-sorted outside of UI (i.e. in Service, Docebo, etc...) or user can click sort descending, so you must
                // compare each time
                compareSwap(searchObjectsMap, item);
              }
            }
            // Update the local courses with the related course id to update the view enrollment
            // options
            this.updateRelatedCourses();

            // Reduce or consolidate duplicate search object courses to appear as one
            // course.
            // Convert map itno array of values with distinct search objects, which has removed
            // the duplicate search objects
            this.reduceSearchObjects(searchObjectsMap);
          },
          error: (error) => {
            console.log(error);
          },
          complete: () => {
            // Force is loading to stop and complete in case of any unhandled errors
            console.log('Recommended Foundational Courses content completed');
          },
        })
      );
    }
  }

  /**
   * Reduce or consolidate duplicate search object courses to appear as one course
   * for Self-Study Courses and Instructor-Led courses.
   * and update session storage for recommended filtered search results
   * @param searchObjectsMap - search object map with string key and value of Search Object
   */
  reduceSearchObjects(searchObjectsMap: Map<string, SearchObject>): void {
    let searchResults: SearchObject[] = [];
    const searchObjectsValues = Array.from(searchObjectsMap.values());
    if (searchObjectsValues && searchObjectsValues.length > 0) {
      searchResults = searchObjectsValues;

      // [BEYOND-1652] - Beyond 3.2.5 - Recommended Foundational Courses - Self-study not appearing from this course link
      this.courses.forEach((course: Course) =>
        searchResults.some(
          (searchResult: SearchObject) =>
            course.id === searchResult.id &&
            (searchResult.relatedCourseIds = course.relatedCourseIds)
        )
      );

      // Reduce search course results that have the same code
      // Update courses to remember sibling courses with the same code.
      const results = JSON.parse(
        sessionStorage.getItem(SessionStorageKeys.RECOMMENDED_FILTERED_SEARCH_RESULTS)
      );
      if (results) {
        sessionStorage.removeItem(SessionStorageKeys.RECOMMENDED_FILTERED_SEARCH_RESULTS);
      }
      if (searchResults && searchResults.length > 0) {
        sessionStorage.setItem(
          SessionStorageKeys.RECOMMENDED_FILTERED_SEARCH_RESULTS,
          JSON.stringify(searchResults)
        );
      }
    }
  }

  /**
   * Update courses and set their related course ids that match
   * TODO: This can use another future refactoring to be cleaner
   */
  updateRelatedCourses(): void {
    if (this.courses && this.courseIds && this.courseIds.length > 0) {
      for (let i = 0; i < this.courseIds.length; i++) {
        const courseId: number = this.courseIds[i];
        if (courseId) {
          const matchingCourse: Course = this.courses.find((item) => item.id === courseId);
          if (matchingCourse) {
            if (matchingCourse.relatedCourseIds) {
              matchingCourse.relatedCourseIds = [];
            }
            if (this.relatedCourseIds && this.relatedCourseIds.length > 0) {
              const relatedCourseId = this.relatedCourseIds[i];
              if (relatedCourseId) {
                matchingCourse.relatedCourseIds = [relatedCourseId];
              }
            }
          }
        }
      }
    }
  }

  /**
   * Load top 3 foundation courses and 1 top certification exam per
   * selected subscription or 4 foundational courses if there is no certification exam
   * NOTE: The course ids and related course ids indices match, so index
   * for course at index 0, has the matching related course id in the related coures ids
   * Except for the last related course ids are -1, indicating certification exams don't have
   * any related courses. There is only 1 exam per course.
   */
  loadCourseIdsBySubscription(): void {
    switch (this.selectedSubscription) {
      case Product.GENESYS_ENGAGE_CLOUD:
      case Product.GENESYS_ENGAGE:
        this.parentSubscriptionId = environment.beyond.engage.subscription_id;
        this.parentSubscriptionSlugName = SubscriptionType.GENESYS_ENGAGE_SUBSCRIPTION;
        this.relatedCourseIds = environment.beyond.engage.related_course_ids;
        this.showViewAllCourses =
          environment.beyond.engage.featureFlags.showRecommendedFoundationalHeading;
        this.showViewAllButton =
          environment.beyond.engage.featureFlags.showRecommendedFoundationalViewAllButton;
        // NOTE: You have to explicitly update this to the Genesys Engage on-premises display name
        this.selectedSubscription = Product.GENESYS_ENGAGE_DISPLAY_NAME;
        break;
      case Product.PURE_CONNECT:
        this.parentSubscriptionId = environment.beyond.pureconnect.subscription_id;
        this.parentSubscriptionSlugName = SubscriptionType.PURECONNECT_SUBSCRIPTION;
        this.relatedCourseIds = environment.beyond.pureconnect.related_course_ids;
        this.showViewAllCourses =
          environment.beyond.pureconnect.featureFlags.showRecommendedFoundationalHeading;
        this.showViewAllButton =
          environment.beyond.pureconnect.featureFlags.showRecommendedFoundationalViewAllButton;
        break;
      case Product.GENESYS_CLOUD:
        this.parentSubscriptionId = environment.beyond.genesys_cloud.subscription_id;
        this.parentSubscriptionSlugName = SubscriptionType.GENESYS_CLOUD_SUBSCRIPTION;
        this.relatedCourseIds = environment.beyond.genesys_cloud.related_course_ids;
        this.showViewAllCourses =
          environment.beyond.genesys_cloud.featureFlags.showRecommendedFoundationalHeading;
        this.showViewAllButton =
          environment.beyond.genesys_cloud.featureFlags.showRecommendedFoundationalViewAllButton;
        break;
      case Product.GENESYS_CLOUD_TRIAL:
        this.parentSubscriptionId = environment.beyond.genesys_cloud_trial.subscription_id;
        this.parentSubscriptionSlugName = SubscriptionType.GENESYS_CLOUD_TRIAL_SUBSCRIPTION;
        this.relatedCourseIds = environment.beyond.genesys_cloud_trial.related_course_ids;
        this.showViewAllCourses =
          environment.beyond.genesys_cloud_trial.featureFlags.showRecommendedFoundationalHeading;
        this.showViewAllButton =
          environment.beyond.genesys_cloud_trial.featureFlags.showRecommendedFoundationalViewAllButton;
        break;
      case Product.GENESYS_CLOUD_PREMIER_PARTNER:
        this.parentSubscriptionId =
          environment.beyond.genesys_cloud_partner_premier.subscription_id;
        this.parentSubscriptionSlugName =
          SubscriptionType.GENESYS_CLOUD_PREMIER_PARTNER_SUBSCRIPTION;
        this.relatedCourseIds = environment.beyond.genesys_cloud_partner_premier.related_course_ids;
        this.showViewAllCourses =
          environment.beyond.genesys_cloud_partner_premier.featureFlags.showRecommendedFoundationalHeading;
        this.showViewAllButton =
          environment.beyond.genesys_cloud_partner_premier.featureFlags.showRecommendedFoundationalViewAllButton;
        break;
      case Product.POINTILLIST:
        this.parentSubscriptionId = environment.beyond.pointillist.subscription_id;
        this.parentSubscriptionSlugName = SubscriptionType.POINTILLIST_SUBSCRIPTION;
        this.showViewAllCourses =
          environment.beyond.pointillist.featureFlags.showRecommendedFoundationalHeading;
        break;
      default:
        console.log('Missing selected subscription', this.selectedSubscription);
        break;
    }
  }

  /**
   * Send the query search label to Google Analytics
   * @param category - the source of the google analytics category string (ex. the page or component triggering the action)
   * @param action - the action to be (ex. click)
   * @param dataLabel - the label associated with the action
   */
  sendGoogleAnalytics(category: string, action: string, dataLabel: string): void {
    logGoogleAnalytics(category, action, dataLabel);
    this.googleAnalyticsService.sendEvent(category, action, dataLabel);
  }

  /**
   * On click handle the incoming route if it is an internal route
   * Assume query params is already in the {} object
   */
  onClick(): void {
    this.sendClick.emit(true);
    this.router.navigate(['search', this.queryParams ? this.queryParams : {}]);
  }

  /*
   * Default session id is set to 0, since eLearning does not have a session id.
   * @param clickEvent - handle event emitted from the search result card component
   * @param instructionType - course instruction type must be eLearning
   * @param courseId - pass the courseId for a given course, in this case an eLearning course
   * TODO: In Beyond 3.1, we should move this to the user service and refactor the search component and
   * course detail to call enrollment service via the user service and remove it from the components
   */
  loadEnrollELearning(clickEvent: any, instructionType: string, courseId: number): void {
    // NOTE: Do not auto enroll users if they click on an internal route that is a link (link title or link description)
    // Load enroll should happen only on route types INTERNAL and from a button route type
    // NOTE: The links click type are string objects, but if you click on the card CTA button it's actually
    // the event object from the route button and object hence you need to check the typeof clickEvent.clickType
    if (
      this.currentUser &&
      clickEvent &&
      clickEvent.isClicked &&
      instructionType &&
      ((clickEvent.clickType &&
        typeof clickEvent.clickType === 'string' &&
        clickEvent.clickType !== RouteTypes.LINK) ||
        (clickEvent.clickType &&
          typeof clickEvent.clickType === 'object' &&
          clickEvent.clickType.clickType !== RouteTypes.LINK &&
          clickEvent.clickType.actionLabel &&
          clickEvent.clickType.actionLabel !== CardTypeCallToActions.REQUEST_A_QUOTE)) &&
      (instructionType === Learning_Type.E_LEARNING ||
        instructionType === Learning_Type.CERTIFICATION_EXAM)
    ) {
      this.enrollmentService
        .enrollSession(this.currentUser.id, courseId)
        .then((response: any) => {
          if (response && response.enrolled && response.enrolled.length > 0) {
            this.userService.loadEnrollments(this.currentUser);
          } else {
            // Show enrollment errors in UI if any
            const errorObj = { errorMessage: response.errors[0] };
          }
        })
        .catch((error) => {
          const errorObj = { errorMessage: error.message };
        });
    }
  }

  /**
   * On Destroy, unsubscribe subscriptions to prevent memory leaks
   */
  ngOnDestroy(): void {
    if (this.subs) {
      this.subs.forEach((sub) => {
        sub.unsubscribe();
      });
    }
  }

  /**
   * Add Track By Course Id to not redraw or re-render list items that have not changed.
   * This improves performance.
   * @param index - Index of Course array
   * @param item - Course item
   * @returns course id
   */
  trackByCourseId(index: number, item: Course): number {
    if (item) {
      return item.id;
    } else {
      return index;
    }
  }

  /**
   *  CardTypeCallToActions enums based on card types
   */
  public get cardTypeCallToActions(): typeof CardTypeCallToActions {
    return CardTypeCallToActions;
  }
}
